import { ContractMessage } from '../../../types/ContractMessage';

type GetPriceValue = (contractId: string) => number | undefined;

const noPrice: GetPriceValue = () => undefined;

/**
 * Groups inherited contracts by supplier id, dropping contracts that do not apply to the given
 * customer or vessel.
 */
export const groupContractsBySupplier = (
    contracts: ContractMessage[],
    customerId: string | undefined,
    vesselId?: string
): Record<string, ContractMessage[]> => {
    return contracts.reduce<Record<string, ContractMessage[]>>((result, contract) => {
        if (!contract.supplierId) {
            return result;
        }

        if (contract.customerIds?.length && customerId && !contract.customerIds.includes(customerId)) {
            return result;
        }

        if (vesselId && contract.excludedVesselIds?.includes(vesselId)) {
            return result;
        }

        result[contract.supplierId] = [...(result[contract.supplierId] ?? []), contract];
        return result;
    }, {});
};

/**
 * Sorts a supplier's contracts: the vessel default contract first (only when the supplier is the
 * vessel default supplier), then by ascending price, then alphabetically by name. Callers without
 * price information can omit `getPriceValue`.
 */
export const sortContractsForSupplier = (
    contracts: ContractMessage[],
    supplierId: string,
    defaultSupplierId: string | undefined,
    vesselDefaultContractId: string | undefined,
    getPriceValue: GetPriceValue = noPrice
): ContractMessage[] => {
    return contracts.toSorted((contract, otherContract) => {
        if (supplierId === defaultSupplierId) {
            if (contract.id === vesselDefaultContractId) {
                return -1;
            }

            if (otherContract.id === vesselDefaultContractId) {
                return 1;
            }
        }

        const contractPrice = getPriceValue(contract.id);
        const otherContractPrice = getPriceValue(otherContract.id);

        if (contractPrice === undefined && otherContractPrice !== undefined) {
            return 1;
        }

        if (contractPrice !== undefined && otherContractPrice === undefined) {
            return -1;
        }

        if (contractPrice !== undefined && otherContractPrice !== undefined && contractPrice !== otherContractPrice) {
            return contractPrice - otherContractPrice;
        }

        return contract.name.localeCompare(otherContract.name);
    });
};

export const getDefaultContractIdForSupplier = (
    supplierId: string,
    contractsBySupplierId: Record<string, ContractMessage[]>,
    defaultSupplierId: string | undefined,
    vesselDefaultContractId: string | undefined,
    getPriceValue: GetPriceValue = noPrice
): string | undefined => {
    const supplierContracts = contractsBySupplierId[supplierId] ?? [];
    return sortContractsForSupplier(
        supplierContracts,
        supplierId,
        defaultSupplierId,
        vesselDefaultContractId,
        getPriceValue
    )[0]?.id;
};
